import { useCallback, useEffect, useRef } from "react";
import {
  convertLatinDigitsInPickerPanel,
  queryDashboardPickerPanelRoots,
} from "./arabicIndicPanelDigits";

/**
 * Merges optional `onOpenChange` with logic that rewrites Latin digits to Eastern Arabic-Indic (٠–٩)
 * inside open Ant Design DatePicker panels (`.dashboard-date-dropdown`).
 *
 * Mutation callbacks are coalesced with `requestAnimationFrame` to avoid redundant full-tree walks.
 */
export function useArabicIndicPickerOpenChange(
  isArabic: boolean,
  userOnOpenChange?: (_open: boolean) => void
): (_open: boolean) => void {
  const observersRef = useRef<MutationObserver[]>([]);
  const rafIdRef = useRef(0);
  const pendingRootsRef = useRef(new Set<HTMLElement>());

  const flushPendingConversions = useCallback(() => {
    rafIdRef.current = 0;
    pendingRootsRef.current.forEach(root => convertLatinDigitsInPickerPanel(root));
    pendingRootsRef.current.clear();
  }, []);

  const scheduleConversion = useCallback(
    (root: HTMLElement) => {
      pendingRootsRef.current.add(root);
      if (rafIdRef.current === 0) {
        rafIdRef.current = requestAnimationFrame(flushPendingConversions);
      }
    },
    [flushPendingConversions]
  );

  const disconnectAll = useCallback(() => {
    if (rafIdRef.current !== 0) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
    pendingRootsRef.current.clear();
    observersRef.current.forEach(o => o.disconnect());
    observersRef.current = [];
  }, []);

  useEffect(() => {
    if (!isArabic) {
      disconnectAll();
    }
  }, [disconnectAll, isArabic]);

  useEffect(() => () => disconnectAll(), [disconnectAll]);

  return useCallback(
    (open: boolean) => {
      userOnOpenChange?.(open);

      if (!isArabic) {
        return;
      }

      if (open) {
        requestAnimationFrame(() => {
          disconnectAll();
          queryDashboardPickerPanelRoots().forEach(root => {
            convertLatinDigitsInPickerPanel(root);
            const observer = new MutationObserver(() => {
              scheduleConversion(root);
            });
            observer.observe(root, {
              subtree: true,
              childList: true,
              characterData: true,
            });
            observersRef.current.push(observer);
          });
        });
      } else {
        disconnectAll();
      }
    },
    [disconnectAll, isArabic, scheduleConversion, userOnOpenChange]
  );
}
